/**
 * Diamond hospital-bed package builder + PDP configurator.
 * Progressive enhancement: reads data-bed-builder-config JSON from the section.
 * Multi-line cart add with shared _bed_package_id properties.
 * Never posts invalid/missing add-on variant IDs — surfaces call-to-add instead.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'diamond_bed_package';

  function qs(root, sel) {
    return root.querySelector(sel);
  }
  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function parseConfig(root) {
    var el = qs(root, '[data-bed-builder-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[diamond-bed] config parse failed', e);
      return null;
    }
  }

  function formatMoney(cents, moneyFormat) {
    if (cents == null || isNaN(cents)) return '—';
    var amount = (Number(cents) / 100).toFixed(2);
    var fmt = moneyFormat || '${{amount}}';
    if (fmt.indexOf('{{amount}}') !== -1) {
      return fmt.replace('{{amount}}', amount);
    }
    if (fmt.indexOf('{{amount_no_decimals}}') !== -1) {
      return fmt.replace('{{amount_no_decimals}}', String(Math.round(Number(cents) / 100)));
    }
    // Fallback: strip Shopify format placeholders
    return '$' + amount;
  }

  function uuid() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return 'bed-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  function readParams() {
    var out = {};
    try {
      var sp = new URLSearchParams(window.location.search);
      ['bed', 'variant', 'size', 'mobility', 'rails', 'mattress', 'trapeze', 'ht', 'wt'].forEach(function (k) {
        if (sp.has(k)) out[k] = sp.get(k);
      });
    } catch (e) {}
    return out;
  }

  function readStorage(key) {
    try {
      var raw = sessionStorage.getItem(key || STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeStorage(key, data) {
    try {
      sessionStorage.setItem(key || STORAGE_KEY, JSON.stringify(data));
    } catch (e) {}
  }

  function checked(root, sel) {
    var el = qs(root, sel + ':checked') || qs(root, sel + '[checked]');
    return el;
  }

  function radioValue(root, nameAttr) {
    var el = qs(root, 'input[name="' + nameAttr + '"]:checked') || qs(root, 'input' + nameAttr);
    // Prefer data attributes selectors
    return null;
  }

  function getCheckedByData(root, dataSel) {
    var nodes = qsa(root, dataSel);
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].checked || nodes[i].getAttribute('checked') != null) {
        if (nodes[i].type === 'hidden') return nodes[i];
        if (nodes[i].checked) return nodes[i];
      }
    }
    // hidden single
    if (nodes.length === 1 && nodes[0].type === 'hidden') return nodes[0];
    return nodes.filter(function (n) {
      return n.checked;
    })[0] || null;
  }

  function pickBed(config, sizeClass) {
    var beds = config.beds || [];
    if (!beds.length) return null;
    var want = (sizeClass || 'standard').toLowerCase();
    var match = beds.filter(function (b) {
      return (b.sizeClass || 'standard').toLowerCase() === want;
    })[0];
    return match || beds[0];
  }

  function recommendSize(config, weight) {
    var thr = Number(config.bariatricWeightThresholdLbs) || 300;
    if (weight == null || weight === '' || isNaN(weight)) return null;
    return Number(weight) >= thr ? 'bariatric' : 'standard';
  }

  function mobilityVariant(bed, mobility) {
    if (!bed || !bed.variants) return null;
    var key = mobility === 'full' ? 'full' : mobility === 'semi' ? 'semi' : 'default';
    return bed.variants[key] || bed.variants.default;
  }

  function addonFor(config, kind, value) {
    var a = config.addons || {};
    if (kind === 'mattress') {
      return value === 'pressure' ? a.mattressPressure : a.mattressStandard;
    }
    if (kind === 'rails') {
      if (value === 'half') return a.railHalf;
      if (value === 'full') return a.railFull;
      return null;
    }
    if (kind === 'trapeze') {
      return value === '1' || value === 1 || value === true ? a.trapeze : null;
    }
    return null;
  }

  function lineFromInputOrAddon(input, addon, label, phone) {
    if (input && input.getAttribute('data-call-if-missing') === 'true') {
      return { status: 'call', price: 0, id: null, title: label, callout: label + ' — call ' + phone + ' to add' };
    }
    if (input && input.getAttribute('data-variant-id') && input.getAttribute('data-available') !== 'false') {
      return {
        status: 'ok',
        price: Number(input.getAttribute('data-price') || 0),
        id: Number(input.getAttribute('data-variant-id')),
        title: (addon && addon.title) || label,
      };
    }
    if (addon && addon.available && addon.id) {
      return { status: 'ok', price: Number(addon.price || 0), id: addon.id, title: addon.title || label };
    }
    return { status: 'call', price: 0, id: null, title: label, callout: label + ' — call ' + phone + ' to add' };
  }

  function buildState(root, config) {
    var heightEl = qs(root, '[data-bed-height]');
    var weightEl = qs(root, '[data-bed-weight]');
    var ht = heightEl && heightEl.value !== '' ? Number(heightEl.value) : null;
    var wt = weightEl && weightEl.value !== '' ? Number(weightEl.value) : null;
    var phone = config.phoneDisplay || '(800) 977-3002';

    var sizeInput = getCheckedByData(root, '[data-bed-size]');
    var mobilityInput = getCheckedByData(root, '[data-bed-mobility]');
    var railsInput = getCheckedByData(root, '[data-bed-rails]');
    var mattressInput = getCheckedByData(root, '[data-bed-mattress]');
    var trapezeInput = getCheckedByData(root, '[data-bed-trapeze]');

    var recSize = recommendSize(config, wt);
    var size =
      (sizeInput && sizeInput.value) ||
      recSize ||
      (config.currentBed && config.currentBed.sizeClass) ||
      'standard';

    var mobility = (mobilityInput && mobilityInput.value) || 'full';
    var rails = (railsInput && railsInput.value) || 'none';
    var mattress = (mattressInput && mattressInput.value) || 'standard';
    var trapeze = (trapezeInput && trapezeInput.value) || '0';

    var bed =
      config.mode === 'pdp' && config.beds && config.beds[0]
        ? config.beds[0]
        : pickBed(config, size);

    var bedVariant = null;
    if (mobilityInput && mobilityInput.getAttribute('data-variant-id')) {
      bedVariant = {
        id: Number(mobilityInput.getAttribute('data-variant-id')),
        price: Number(mobilityInput.getAttribute('data-price') || 0),
        available: mobilityInput.getAttribute('data-available') !== 'false',
        title: mobility === 'full' ? 'Full-Electric' : mobility === 'semi' ? 'Semi-Electric' : 'Bed',
      };
    } else {
      bedVariant = mobilityVariant(bed, mobility);
    }

    var callouts = [];
    var total = bedVariant && bedVariant.available !== false ? Number(bedVariant.price || 0) : 0;
    if (bedVariant && bedVariant.available === false) {
      callouts.push('Selected bed type is unavailable — call to confirm stock.');
    }

    var railsLine =
      rails === 'none'
        ? { status: 'none', price: 0, id: null, title: 'None' }
        : lineFromInputOrAddon(
            railsInput,
            addonFor(config, 'rails', rails),
            rails === 'full' ? 'Full-length rails' : 'Half-length rails',
            phone
          );

    var mattressLine = lineFromInputOrAddon(
      mattressInput,
      addonFor(config, 'mattress', mattress),
      mattress === 'pressure' ? 'Pressure-reducing mattress' : 'Standard mattress',
      phone
    );

    var trapezeLine = { status: 'none', price: 0, id: null, title: 'No' };
    if (trapeze === '1') {
      trapezeLine = lineFromInputOrAddon(trapezeInput, addonFor(config, 'trapeze', '1'), 'Trapeze bar', phone);
    }

    [railsLine, mattressLine, trapezeLine].forEach(function (line) {
      if (line && line.status === 'ok') total += Number(line.price || 0);
      if (line && line.callout) callouts.push(line.callout);
    });

    callouts = callouts.filter(function (c, i, arr) {
      return arr.indexOf(c) === i;
    });

    return {
      ht: ht,
      wt: wt,
      recSize: recSize,
      size: size,
      mobility: mobility,
      rails: rails,
      mattress: mattress,
      trapeze: trapeze,
      bed: bed,
      bedVariant: bedVariant,
      railsLine: railsLine,
      mattressLine: mattressLine,
      trapezeLine: trapezeLine,
      total: total,
      callouts: callouts,
    };
  }

  function setStrong(li, text) {
    if (!li) return;
    var strong = li.querySelector('strong');
    if (strong) strong.textContent = text;
    else li.innerHTML = li.textContent.split(':')[0] + ': <strong>' + text + '</strong>';
  }

  /**
   * Package tile slots: fill/empty from real product images (no composites).
   * opts: { filled, image, title, note, badge, emptyLabel }
   */
  function setPackageSlot(root, role, opts) {
    var slot = qs(root, '[data-package-slot="' + role + '"]');
    if (!slot) return;
    opts = opts || {};
    var img = qs(slot, '[data-slot-img]');
    var name = qs(slot, '[data-slot-name]');
    var empty = qs(slot, '[data-slot-empty]');
    var note = qs(slot, '[data-slot-note]');
    var badge = qs(slot, '[data-slot-badge]');
    var filled = !!opts.filled;

    slot.classList.toggle('is-filled', filled);
    slot.classList.toggle('is-empty', !filled);

    if (filled) {
      if (img) {
        if (opts.image) {
          if (img.getAttribute('src') !== opts.image) img.src = opts.image;
          img.alt = opts.title || '';
          img.hidden = false;
        } else {
          img.hidden = true;
          img.removeAttribute('src');
          img.alt = '';
        }
      }
      if (name) name.textContent = opts.title || '';
      if (empty) empty.hidden = true;
    } else {
      if (img) {
        img.hidden = true;
        img.removeAttribute('src');
        img.alt = '';
      }
      if (name) name.textContent = '';
      if (empty) {
        empty.hidden = false;
        if (opts.emptyLabel) empty.textContent = opts.emptyLabel;
      }
    }

    if (note) {
      if (opts.note) {
        note.textContent = opts.note;
        note.hidden = false;
      } else {
        note.textContent = '';
        note.hidden = true;
      }
    }
    if (badge) {
      if (opts.badge) {
        badge.textContent = opts.badge;
        badge.hidden = false;
      } else {
        badge.textContent = '';
        badge.hidden = true;
      }
    }
  }

  function renderPackageTiles(root, config, state) {
    if (!qs(root, '[data-package-tiles]')) return;
    var addons = config.addons || {};

    var mobLabel =
      state.mobility === 'full'
        ? 'Full-Electric'
        : state.mobility === 'semi'
          ? 'Semi-Electric'
          : (state.bedVariant && state.bedVariant.title) || '';

    setPackageSlot(root, 'bed', {
      filled: !!(state.bed && (state.bed.image || state.bed.title)),
      image: (state.bed && state.bed.image) || null,
      title: (state.bed && state.bed.title) || '',
      badge: mobLabel || null,
      note:
        state.bedVariant && state.bedVariant.available === false
          ? 'Call to confirm stock'
          : null,
    });

    var mattAddon =
      state.mattress === 'pressure' ? addons.mattressPressure : addons.mattressStandard;
    var mattTitle =
      (state.mattressLine && state.mattressLine.title) ||
      (mattAddon && mattAddon.title) ||
      (state.mattress === 'pressure' ? 'Pressure-reducing' : 'Standard');
    var mattImage = (mattAddon && mattAddon.image) || null;
    setPackageSlot(root, 'mattress', {
      filled: true,
      image: mattImage,
      title: mattTitle,
      note: state.mattressLine && state.mattressLine.status === 'call' ? 'Call to add' : null,
    });

    if (state.rails === 'none') {
      setPackageSlot(root, 'rails', {
        filled: false,
        emptyLabel: 'None',
      });
    } else {
      var railAddon = state.rails === 'full' ? addons.railFull : addons.railHalf;
      var railTitle =
        (railAddon && railAddon.title) ||
        (state.rails === 'full' ? 'Full-length rails' : 'Half-length rails');
      setPackageSlot(root, 'rails', {
        filled: true,
        image: (railAddon && railAddon.image) || null,
        title: railTitle,
        note: state.railsLine && state.railsLine.status === 'call' ? 'Call to add' : null,
      });
    }

    if (state.trapeze !== '1') {
      setPackageSlot(root, 'trapeze', {
        filled: false,
        emptyLabel: 'No trapeze',
      });
    } else {
      var trapAddon = addons.trapeze;
      setPackageSlot(root, 'trapeze', {
        filled: true,
        image: (trapAddon && trapAddon.image) || null,
        title: (trapAddon && trapAddon.title) || 'Trapeze bar',
        note: state.trapezeLine && state.trapezeLine.status === 'call' ? 'Call to add' : null,
      });
    }
  }

  function render(root, config, state) {
    var sumBed = qs(root, '[data-sum-bed]');
    var sumMob = qs(root, '[data-sum-mobility]');
    var sumRails = qs(root, '[data-sum-rails]');
    var sumMatt = qs(root, '[data-sum-mattress]');
    var sumTrap = qs(root, '[data-sum-trapeze]');
    var totalEl = qs(root, '[data-bed-total]');
    var calloutsEl = qs(root, '[data-bed-callouts]');
    var hint = qs(root, '[data-size-hint]');
    var cont = qs(root, '[data-bed-continue]');
    var switchEl = qs(root, '[data-bed-switch]');

    if (state.bed) {
      setStrong(sumBed, state.bed.title || '—');
    }

    // Legacy single-image stage (older markup)
    var mediaWrap = qs(root, '[data-bed-summary-media]');
    var mediaImg = qs(root, '[data-bed-summary-img]');
    var stageLabel = qs(root, '[data-bed-stage-label]');
    if (mediaWrap && mediaImg) {
      var imgSrc = (state.bed && state.bed.image) || null;
      if (imgSrc) {
        if (mediaImg.getAttribute('src') !== imgSrc) {
          mediaImg.src = imgSrc;
        }
        mediaImg.alt = (state.bed && state.bed.title) || '';
        mediaWrap.hidden = false;
      }
    }
    if (stageLabel && state.bed && state.bed.title) {
      stageLabel.textContent = state.bed.title;
    }

    renderPackageTiles(root, config, state);

    var mobLabel =
      state.mobility === 'full' ? 'Full-Electric' : state.mobility === 'semi' ? 'Semi-Electric' : state.bedVariant && state.bedVariant.title ? state.bedVariant.title : '—';
    setStrong(sumMob, mobLabel);
    setStrong(
      sumRails,
      state.rails === 'none' ? 'None' : state.railsLine.status === 'call' ? (state.rails === 'full' ? 'Full-length (call to add)' : 'Half-length (call to add)') : state.rails === 'full' ? 'Full-length' : 'Half-length'
    );
    setStrong(
      sumMatt,
      state.mattressLine.status === 'call'
        ? (state.mattress === 'pressure' ? 'Pressure-reducing (call to add)' : 'Standard (call to add)')
        : state.mattressLine.title || (state.mattress === 'pressure' ? 'Pressure-reducing' : 'Standard')
    );
    setStrong(sumTrap, state.trapeze === '1' ? (state.trapezeLine.status === 'call' ? 'Yes (call to add)' : 'Yes') : 'No');

    if (totalEl) totalEl.textContent = formatMoney(state.total, config.moneyFormat);
    if (calloutsEl) {
      calloutsEl.textContent = state.callouts.length ? state.callouts.join(' · ') : '';
    }

    if (hint) {
      if (state.recSize) {
        hint.innerHTML =
          'Recommended size: <strong>' +
          (state.recSize === 'bariatric' ? 'Bariatric / wide' : 'Standard') +
          '</strong>' +
          (state.wt != null ? ' (from ' + state.wt + ' lbs)' : '') +
          '.';
      }
    }

    if (switchEl && config.mode === 'pdp' && config.altBeds && state.recSize) {
      var current = (config.currentBed && config.currentBed.sizeClass) || 'standard';
      if (state.recSize !== current && config.altBeds[state.recSize]) {
        var alt = config.altBeds[state.recSize];
        switchEl.hidden = false;
        switchEl.innerHTML =
          'Recommendation points to a different bed: <a href="' +
          alt.url +
          buildQuery(state, config, alt.handle) +
          '">' +
          (alt.title || 'View recommended bed') +
          '</a>.';
      } else {
        switchEl.hidden = true;
        switchEl.textContent = '';
      }
    }

    if (cont && state.bed) {
      cont.href = state.bed.url + buildQuery(state, config, state.bed.handle) + '#DiamondBedPackage';
      cont.classList.remove('is-disabled');
    } else if (cont) {
      cont.classList.add('is-disabled');
    }

    var atcBtn = qs(root, '[data-bed-add-package]');
    if (atcBtn) {
      var canAdd = !!(state.bedVariant && state.bedVariant.id && state.bedVariant.available !== false);
      atcBtn.disabled = !canAdd;
      if (!canAdd) atcBtn.classList.add('is-disabled');
      else atcBtn.classList.remove('is-disabled');
    }

    var recipe = {
      bed: state.bed && state.bed.handle,
      variant: state.bedVariant && state.bedVariant.id,
      size: state.size,
      mobility: state.mobility,
      rails: state.rails,
      mattress: state.mattress,
      trapeze: state.trapeze,
      ht: state.ht,
      wt: state.wt,
      total: state.total,
    };
    writeStorage(config.storageKey || STORAGE_KEY, recipe);
    return recipe;
  }

  function buildQuery(state, config, bedHandle) {
    var p = new URLSearchParams();
    // Force flagship-beds template until Admin assigns it as default
    var view = config.productView || 'flagship-beds';
    if (view) p.set('view', view);
    if (bedHandle) p.set('bed', bedHandle);
    if (state.bedVariant && state.bedVariant.id) p.set('variant', String(state.bedVariant.id));
    p.set('size', state.size || 'standard');
    p.set('mobility', state.mobility || 'full');
    p.set('rails', state.rails || 'none');
    p.set('mattress', state.mattress || 'standard');
    p.set('trapeze', state.trapeze || '0');
    if (state.ht != null && !isNaN(state.ht)) p.set('ht', String(state.ht));
    if (state.wt != null && !isNaN(state.wt)) p.set('wt', String(state.wt));
    var s = p.toString();
    return s ? '?' + s : '';
  }

  function applyIncoming(root, config) {
    var params = readParams();
    var stored = readStorage(config.storageKey || STORAGE_KEY) || {};
    var src = Object.assign({}, stored, params);

    function setRadio(sel, value) {
      if (value == null || value === '') return;
      qsa(root, sel).forEach(function (input) {
        if (String(input.value) === String(value)) {
          input.checked = true;
        }
      });
    }

    if (src.ht != null) {
      var h = qs(root, '[data-bed-height]');
      if (h) h.value = src.ht;
    }
    if (src.wt != null) {
      var w = qs(root, '[data-bed-weight]');
      if (w) w.value = src.wt;
    }
    setRadio('[data-bed-size]', src.size);
    setRadio('[data-bed-mobility]', src.mobility);
    setRadio('[data-bed-rails]', src.rails);
    setRadio('[data-bed-mattress]', src.mattress);
    setRadio('[data-bed-trapeze]', src.trapeze);

    // Variant deep-link on PDP
    if (src.variant && config.mode === 'pdp') {
      qsa(root, '[data-bed-mobility]').forEach(function (input) {
        if (String(input.getAttribute('data-variant-id')) === String(src.variant)) {
          input.checked = true;
        }
      });
    }
  }

  function packageItems(state) {
    var packageId = uuid();
    var items = [];
    if (state.bedVariant && state.bedVariant.id && state.bedVariant.available !== false) {
      items.push({
        id: state.bedVariant.id,
        quantity: 1,
        properties: {
          _bed_package_id: packageId,
          'Package role': 'Bed',
          Package: 'Hospital bed package',
        },
      });
    }
    if (state.mattressLine && state.mattressLine.status === 'ok' && state.mattressLine.id) {
      items.push({
        id: state.mattressLine.id,
        quantity: 1,
        properties: {
          _bed_package_id: packageId,
          'Package role': 'Mattress',
          Package: 'Hospital bed package',
        },
      });
    }
    if (state.railsLine && state.railsLine.status === 'ok' && state.railsLine.id) {
      items.push({
        id: state.railsLine.id,
        quantity: 1,
        properties: {
          _bed_package_id: packageId,
          'Package role': 'Rails',
          Package: 'Hospital bed package',
        },
      });
    }
    if (state.trapezeLine && state.trapezeLine.status === 'ok' && state.trapezeLine.id) {
      items.push({
        id: state.trapezeLine.id,
        quantity: 1,
        properties: {
          _bed_package_id: packageId,
          'Package role': 'Trapeze',
          Package: 'Hospital bed package',
        },
      });
    }
    return { packageId: packageId, items: items };
  }

  function addPackage(root, config, state) {
    var status = qs(root, '[data-bed-status]');
    var btn = qs(root, '[data-bed-add-package]');
    var built = packageItems(state);
    if (!built.items.length) {
      if (status) status.textContent = 'Nothing available to add. Call ' + (config.phoneDisplay || '') + '.';
      return;
    }

    if (btn) btn.disabled = true;
    if (status) status.textContent = 'Adding package…';

    var url = config.cartAddUrl || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js');
    if (url.indexOf('.js') === -1 && url.indexOf('add') !== -1) {
      // routes.cart_add_url is often /cart/add — append .js
      url = url.replace(/\/?$/, '') + '.js';
    }

    var sectionIds = [];
    qsa(document, '[data-section-id]').forEach(function (el) {
      var id = el.getAttribute('data-section-id');
      if (id && sectionIds.indexOf(id) === -1) sectionIds.push(id);
    });
    // Common Horizon cart chrome ids when present in DOM
    qsa(document, 'cart-items-component, cart-drawer-component, cart-icon').forEach(function (el) {
      var sid = el.closest('[id^="shopify-section-"]');
      if (sid && sid.id) {
        var clean = sid.id.replace('shopify-section-', '');
        if (sectionIds.indexOf(clean) === -1) sectionIds.push(clean);
      }
    });

    var payload = { items: built.items };
    if (sectionIds.length) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
      credentials: 'same-origin',
    })
      .then(function (res) {
        return res.json().then(function (data) {
          return { ok: res.ok, status: res.status, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok || result.data.status) {
          var msg = (result.data && (result.data.description || result.data.message)) || 'Could not add package.';
          if (status) status.textContent = msg;
          if (btn) btn.disabled = false;
          document.dispatchEvent(
            new CustomEvent('cart:error', {
              bubbles: true,
              detail: { source: 'diamond-bed-package', message: msg },
            })
          );
          return;
        }

        if (status) {
          var n = built.items.length;
          var note = 'Added ' + n + ' item' + (n === 1 ? '' : 's') + ' to cart';
          if (state.callouts.length) {
            note += '. Some options still need a specialist: call ' + (config.phoneDisplay || '');
          } else {
            note += '.';
          }
          status.textContent = note;
        }
        if (btn) btn.disabled = false;

        // Horizon cart UI listens for cart:update (CartAddEvent.eventName)
        var sections = (result.data && result.data.sections) || {};
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: result.data,
              sourceId: 'diamond-bed-package',
              data: {
                source: 'diamond-bed-package',
                itemCount: built.items.length,
                sections: sections,
              },
            },
          })
        );

        // If sections were empty, still nudge cart chrome to re-fetch
        if (!sections || !Object.keys(sections).length) {
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: result.data,
                sourceId: 'diamond-bed-package-refresh',
                data: { source: 'diamond-bed-package', sections: {} },
              },
            })
          );
        }

        try {
          var openCart = document.querySelector('cart-drawer-component');
          if (openCart && typeof openCart.open === 'function') openCart.open();
          else if (openCart && typeof openCart.showDialog === 'function') openCart.showDialog();
        } catch (e) {}
      })
      .catch(function (err) {
        console.error(err);
        if (status) status.textContent = 'Network error adding package. Please call us.';
        if (btn) btn.disabled = false;
      });
  }

  function bindTips(root) {
    qsa(root, '[data-bed-tip-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var id = btn.getAttribute('aria-controls');
        var panel = id ? document.getElementById(id) : null;
        if (!panel) return;
        var open = btn.getAttribute('aria-expanded') === 'true';
        // Close other tips in this root
        qsa(root, '[data-bed-tip-toggle]').forEach(function (other) {
          if (other === btn) return;
          other.setAttribute('aria-expanded', 'false');
          var oid = other.getAttribute('aria-controls');
          var op = oid ? document.getElementById(oid) : null;
          if (op) op.hidden = true;
        });
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        panel.hidden = open;
      });
    });
  }

  function bind(root) {
    var config = parseConfig(root);
    if (!config) return;

    bindTips(root);
    applyIncoming(root, config);

    function update() {
      var state = buildState(root, config);
      // Auto-select size radio when weight suggests bariatric and user hasn't locked? Soft: only update hint + optional size radio when weight changes from empty
      if (state.recSize && config.mode === 'builder') {
        var sizeRadios = qsa(root, '[data-bed-size]');
        if (sizeRadios.length && state.wt != null) {
          sizeRadios.forEach(function (r) {
            r.checked = r.value === state.recSize;
          });
          state = buildState(root, config);
        }
      }
      render(root, config, state);
      return state;
    }

    qsa(root, 'input').forEach(function (input) {
      input.addEventListener('change', update);
      input.addEventListener('input', update);
    });

    var atc = qs(root, '[data-bed-add-package]');
    if (atc) {
      atc.addEventListener('click', function () {
        var state = update();
        addPackage(root, config, state);
      });
    }

    var cont = qs(root, '[data-bed-continue]');
    if (cont) {
      cont.addEventListener('click', function () {
        update(); // refresh href + sessionStorage before navigation
      });
    }

    update();

    // Landed from builder with package params — scroll to configurator once
    if (config.mode === 'pdp' && (window.location.hash === '#DiamondBedPackage' || window.location.search.indexOf('mattress=') !== -1)) {
      var pkg = document.getElementById('DiamondBedPackage');
      if (pkg && typeof pkg.scrollIntoView === 'function') {
        setTimeout(function () {
          pkg.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
      }
    }
  }

  function init() {
    qsa(document, '[data-diamond-bed-builder], [data-diamond-bed-package]').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
